import { Component, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { TrialSignupOptions } from 'src/app/core-module/models/Trial/trial-signup-options';
import { BasicResult } from 'src/app/core-module/models/basic-result';
import { Toastr } from 'src/app/core-module/models/Utils/toastr';
import { ToastrService } from 'src/app/core-module/services/toastr.service';
import { UserManagementService } from 'src/app/core-module/services/usermanagement.service';

@Component({
  selector: 'app-show-trial',
  templateUrl: './show-trial.component.html',
  styleUrl: './show-trial.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ShowTrialComponent {
  public showPage1: boolean = true;
  public showPage2: boolean = false;
  public showThanks: boolean = false;
  public trialOptions: TrialSignupOptions = new TrialSignupOptions({ Plan: 1 });

  public validators: ShowTrialValidator = new ShowTrialValidator();
  public orgNameTouched: boolean = false;
  public orgEmailTouched: boolean = false;
  public orgNumberTouched: boolean = false;
  public orgWebTouched: boolean = false;
  public contactNameTouched: boolean = false;
  public contactEmailTouched: boolean = false;
  public submitting: boolean = false;

  constructor(private user: UserManagementService, private toast: ToastrService) {
    this.validators.orgName = new FormControl(this.trialOptions.OrganisationName, [Validators.required, Validators.minLength(5)]);
    this.validators.orgEmail = new FormControl(this.trialOptions.OrganisationEmail, [Validators.required, Validators.email]);
    this.validators.orgNumber = new FormControl(this.trialOptions.OrganisationPhone, [Validators.required, Validators.minLength(10), Validators.pattern('^[0-9 ()]*$')]);
    this.validators.orgWeb = new FormControl(this.trialOptions.OrganisationWebsite, [Validators.minLength(3)]);
    this.validators.contactEmail = new FormControl(this.trialOptions.ContactEmail, [Validators.required, Validators.email]);
    this.validators.contactName = new FormControl(this.trialOptions.ContactName, [Validators.required, Validators.minLength(5)]);
  }

  public page2() {
    this.page1ValidateUpdate(5);
    if (!this.validators.isPage1Valid()) {
      this.toast.addToastr(new Toastr({ Type: 3, Message: 'Please complete the required fields.' }));
      this.orgNameTouched = true;
      this.orgEmailTouched = true;
      this.orgNumberTouched = true;
      return;
    }

    this.showPage1 = false;
    this.showPage2 = true;
  }

  public page1() {
    this.showPage1 = true;
    this.showPage2 = false;
  }

  public createShowTrial() {
    this.page1ValidateUpdate(5);
    this.page2ValidateUpdate(3);
    if (!this.validators.isValid()) {
      this.toast.addToastr(new Toastr({ Type: 3, Message: 'Please complete the required fields.' }));
      this.orgNameTouched = true;
      this.orgEmailTouched = true;
      this.orgNumberTouched = true;
      this.contactEmailTouched = true;
      this.contactNameTouched = true;
      return;
    }

    this.submitting = true;
    this.user.createShowTrial(this.trialOptions).subscribe({
      next: (res: BasicResult) => {
        this.submitting = false;
        if (res.IsError) {
          this.handleSignupError(res);
          return;
        }
        this.showPage1 = false;
        this.showPage2 = false;
        this.showThanks = true;
      },
      error: () => {
        this.submitting = false;
        this.toast.addToastr(new Toastr({ Type: 3, Message: 'An error occurred creating your trial, please try again later.' }));
      }
    });
  }

  public page1ValidateUpdate(input: number) {
    switch (input) {
      case 1:
        this.validators.orgName.setValue(this.trialOptions.OrganisationName);
        this.orgNameTouched = true;
        break;
      case 2:
        this.validators.orgEmail.setValue(this.trialOptions.OrganisationEmail);
        this.orgEmailTouched = true;
        break;
      case 3:
        this.validators.orgNumber.setValue(this.trialOptions.OrganisationPhone);
        this.orgNumberTouched = true;
        break;
      case 4:
        this.validators.orgWeb.setValue(this.trialOptions.OrganisationWebsite);
        this.orgWebTouched = true;
        break;
      case 5:
        this.validators.orgName.setValue(this.trialOptions.OrganisationName);
        this.validators.orgEmail.setValue(this.trialOptions.OrganisationEmail);
        this.validators.orgNumber.setValue(this.trialOptions.OrganisationPhone);
        this.validators.orgWeb.setValue(this.trialOptions.OrganisationWebsite);
        break;
    }
  }

  public page2ValidateUpdate(input: number) {
    switch (input) {
      case 1:
        this.validators.contactName.setValue(this.trialOptions.ContactName);
        this.contactNameTouched = true;
        break;
      case 2:
        this.validators.contactEmail.setValue(this.trialOptions.ContactEmail);
        this.contactEmailTouched = true;
        break;
      case 3:
        this.validators.contactName.setValue(this.trialOptions.ContactName);
        this.validators.contactEmail.setValue(this.trialOptions.ContactEmail);
        break;
    }
  }

  private handleSignupError(res: BasicResult) {
    if (res.ErrorCode === 'ExistingAccount') {
      this.toast.addToastr(new Toastr({ Type: 3, Message: 'An account already exists for this email address.' }));
      return;
    }
    if (res.ErrorCode === 'InvalidRequest') {
      this.toast.addToastr(new Toastr({ Type: 3, Message: 'Please complete the required fields.' }));
      return;
    }
    this.toast.addToastr(new Toastr({ Type: 3, Message: 'An error occurred creating your trial, please try again later.' }));
  }
}

class ShowTrialValidator {
  orgName: FormControl;
  orgEmail: FormControl;
  orgNumber: FormControl;
  orgWeb: FormControl;
  contactName: FormControl;
  contactEmail: FormControl;

  isValid() {
    return this.orgName.valid && this.orgEmail.valid && this.orgNumber.valid && this.orgWeb.valid && this.contactEmail.valid && this.contactName.valid;
  }

  isPage1Valid() {
    return this.orgName.valid && this.orgEmail.valid && this.orgNumber.valid && this.orgWeb.valid;
  }
}
